import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { api } from "../api/apiClient";
import type { BlogPostDetail, BlogPostStatus } from "../api/types";
import { Modal } from "../components/Modal";

const STATUS_CLASS: Record<BlogPostStatus, string> = {
  NEW: "badge muted",
  GENERATING: "badge primary",
  DRAFT_READY: "badge primary",
  EDITING: "badge primary",
  PUBLISHING: "badge primary",
  POSTED: "badge success",
  FAILED: "badge danger",
};

const STATUS_LABEL: Record<BlogPostStatus, string> = {
  NEW: "Queued",
  GENERATING: "Generating",
  DRAFT_READY: "Draft ready",
  EDITING: "AI editing",
  PUBLISHING: "Publishing",
  POSTED: "Posted",
  FAILED: "Failed",
};

type Tab = "draft" | "research" | "timeline";

const POLL_INTERVAL_MS = 5000;
const NON_TERMINAL: BlogPostStatus[] = [
  "NEW",
  "GENERATING",
  "EDITING",
  "PUBLISHING",
];

function formatDateTime(iso: string | null): string {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return "—";
  }
}

export function BlogPostDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [post, setPost] = useState<BlogPostDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [tab, setTab] = useState<Tab>("draft");
  const [deleting, setDeleting] = useState(false);
  const [busy, setBusy] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [publishModalOpen, setPublishModalOpen] = useState(false);
  const [editNotes, setEditNotes] = useState("");

  const load = useCallback(async () => {
    if (!id) return;
    try {
      const data = await api.getBlogPost(id);
      setPost(data);
      setError(null);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : "Failed to load blog post");
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    void load();
  }, [load]);

  // Poll while the post is in a non-terminal state.
  useEffect(() => {
    if (!post) return;
    if (!NON_TERMINAL.includes(post.status)) return;
    const handle = window.setInterval(() => {
      void load();
    }, POLL_INTERVAL_MS);
    return () => window.clearInterval(handle);
  }, [post, load]);

  async function handleDelete() {
    if (!post) return;
    if (!window.confirm("Delete this blog post? This cannot be undone.")) return;
    setDeleting(true);
    try {
      await api.deleteBlogPost(post.id);
      navigate("/app/blog-posts");
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : "Could not delete blog post");
      setDeleting(false);
    }
  }

  async function handleRegenerate() {
    if (!post) return;
    if (
      !window.confirm(
        "Regenerate this article from scratch? The current draft will be replaced.",
      )
    ) {
      return;
    }
    setBusy(true);
    setActionError(null);
    try {
      const updated = await api.regenerateBlogPost(post.id);
      setPost(updated);
    } catch (e: unknown) {
      setActionError(e instanceof Error ? e.message : "Could not regenerate");
    } finally {
      setBusy(false);
    }
  }

  async function handleSubmitEdit() {
    if (!post) return;
    if (!editNotes.trim()) return;
    setBusy(true);
    setActionError(null);
    try {
      const updated = await api.requestBlogPostEdit(post.id, editNotes.trim());
      setPost(updated);
      setEditModalOpen(false);
      setEditNotes("");
    } catch (e: unknown) {
      setActionError(e instanceof Error ? e.message : "Could not request edit");
    } finally {
      setBusy(false);
    }
  }

  async function handleConfirmPublish() {
    if (!post) return;
    setBusy(true);
    setActionError(null);
    try {
      const updated = await api.publishBlogPost(post.id);
      setPost(updated);
      setPublishModalOpen(false);
    } catch (e: unknown) {
      setActionError(e instanceof Error ? e.message : "Could not publish");
    } finally {
      setBusy(false);
    }
  }

  if (loading) {
    return (
      <section>
        <p style={{ color: "var(--color-text-muted)" }}>Loading blog post…</p>
      </section>
    );
  }

  if (error || !post) {
    return (
      <section style={{ display: "flex", flexDirection: "column", gap: "var(--space-3)" }}>
        <Link to="/app/blog-posts">← Back to blog posts</Link>
        <div className="alert error">{error ?? "Blog post not found"}</div>
      </section>
    );
  }

  const linkList = post.internalLinks
    .split(/[\n,]/)
    .map((s) => s.trim())
    .filter(Boolean);

  return (
    <section style={{ display: "flex", flexDirection: "column", gap: "var(--space-4)" }}>
      <div>
        <Link to="/app/blog-posts">← Back to blog posts</Link>
      </div>

      <div
        className="card"
        style={{ padding: "var(--space-4)", display: "flex", flexDirection: "column", gap: "var(--space-3)" }}
      >
        <div style={{ display: "flex", justifyContent: "space-between", gap: "var(--space-3)", flexWrap: "wrap" }}>
          <div>
            <h2 className="page-title" style={{ margin: 0 }}>
              {post.idea}
            </h2>
            <p
              className="page-subtitle"
              style={{
                margin: "var(--space-1) 0 0",
                display: "flex",
                gap: "var(--space-2)",
                flexWrap: "wrap",
                alignItems: "center",
              }}
            >
              <span style={{ textTransform: "uppercase" }}>{post.language}</span>
              <span>· {post.tone.toLowerCase()}</span>
              <span>· {post.targetWordCount} words target</span>
              <span>· by {post.createdBy.fullName}</span>
            </p>
          </div>
          <div style={{ display: "flex", gap: "var(--space-2)", alignItems: "center" }}>
            <span className={STATUS_CLASS[post.status]}>{STATUS_LABEL[post.status]}</span>
            {post.shopifyArticleUrl ? (
              <a
                href={post.shopifyArticleUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="button tinted"
              >
                View on Shopify ↗
              </a>
            ) : null}
            <button
              type="button"
              className="button plain"
              onClick={handleDelete}
              disabled={deleting}
              style={{ color: "var(--color-danger, #c00)" }}
            >
              {deleting ? "Deleting…" : "Delete"}
            </button>
          </div>
        </div>

        {post.lastEvent ? (
          <p style={{ margin: 0, color: "var(--color-text-muted)" }}>
            <strong>Last event:</strong> {post.lastEvent}
          </p>
        ) : null}

        {post.errorMessage ? (
          <div className="alert error">
            <strong>Error:</strong> {post.errorMessage}
          </div>
        ) : null}

        {actionError ? <div className="alert error">{actionError}</div> : null}

        <div style={{ display: "flex", gap: "var(--space-2)", flexWrap: "wrap" }}>
          {post.status === "DRAFT_READY" || post.status === "FAILED" ? (
            <button
              type="button"
              className="button tinted"
              onClick={handleRegenerate}
              disabled={busy}
            >
              {busy ? "Working…" : "Regenerate from scratch"}
            </button>
          ) : null}
          {post.status === "DRAFT_READY" || post.status === "FAILED" ? (
            <button
              type="button"
              className="button tinted"
              onClick={() => {
                setEditNotes("");
                setActionError(null);
                setEditModalOpen(true);
              }}
              disabled={busy}
            >
              Request AI edit
            </button>
          ) : null}
          {post.status === "DRAFT_READY" ? (
            <button
              type="button"
              className="button"
              onClick={() => {
                setActionError(null);
                setPublishModalOpen(true);
              }}
              disabled={busy}
            >
              Publish to Shopify
            </button>
          ) : null}
        </div>
      </div>

      <div style={{ display: "flex", gap: "var(--space-1)", borderBottom: "1px solid var(--color-border, #ddd)" }}>
        {(["draft", "research", "timeline"] as Tab[]).map((t) => (
          <button
            key={t}
            type="button"
            onClick={() => setTab(t)}
            className={tab === t ? "button" : "button plain"}
            style={{
              borderRadius: 0,
              borderBottom: tab === t ? "2px solid var(--color-primary, #333)" : "2px solid transparent",
            }}
          >
            {t === "draft" ? "Draft" : t === "research" ? "Research & process" : "Timeline"}
          </button>
        ))}
      </div>

      {tab === "draft" ? (
        <DraftTab post={post} />
      ) : tab === "research" ? (
        <ResearchTab post={post} linkList={linkList} />
      ) : (
        <TimelineTab post={post} />
      )}

      {editModalOpen ? (
        <Modal title="Request AI edit" onClose={() => setEditModalOpen(false)}>
          <div style={{ display: "flex", flexDirection: "column", gap: "var(--space-3)" }}>
            <p style={{ margin: 0, color: "var(--color-text-muted)" }}>
              Describe what you want changed. The writer revises the existing draft in the same
              session, so it still knows its research; the notes are kept in the timeline.
            </p>
            <textarea
              rows={5}
              placeholder="e.g. Trim the intro to two sentences. Make the tone less formal. Add a paragraph about peonies."
              value={editNotes}
              onChange={(e) => setEditNotes(e.target.value)}
            />
            {actionError ? <div className="alert error">{actionError}</div> : null}
            <div style={{ display: "flex", gap: "var(--space-2)", justifyContent: "flex-end" }}>
              <button
                type="button"
                className="button tinted"
                onClick={() => setEditModalOpen(false)}
                disabled={busy}
              >
                Cancel
              </button>
              <button
                type="button"
                className="button"
                onClick={handleSubmitEdit}
                disabled={busy || !editNotes.trim()}
              >
                {busy ? "Submitting…" : "Send to AI"}
              </button>
            </div>
          </div>
        </Modal>
      ) : null}

      {publishModalOpen ? (
        <Modal title="Publish to Shopify" onClose={() => setPublishModalOpen(false)}>
          <div style={{ display: "flex", flexDirection: "column", gap: "var(--space-3)" }}>
            <p style={{ margin: 0 }}>
              You are about to publish <strong>{post.idea}</strong> to the Shopify storefront.
              The article will go live immediately. This cannot be undone.
            </p>
            <div
              style={{
                padding: "var(--space-3)",
                background: "var(--color-surface-muted, #f9f9f9)",
                borderRadius: "var(--radius-md, 8px)",
                maxHeight: 240,
                overflow: "auto",
              }}
            >
              <strong>Final preview (first 500 chars):</strong>
              <pre style={{ whiteSpace: "pre-wrap", fontFamily: "inherit", margin: "var(--space-2) 0 0" }}>
                {post.draftMarkdown.slice(0, 500)}
                {post.draftMarkdown.length > 500 ? "…" : ""}
              </pre>
            </div>
            {post.metaTitle ? (
              <div style={{ color: "var(--color-text-muted)" }}>
                Published as <strong>{post.metaTitle}</strong>
                {post.slug ? <> at <code>/blogs/news/{post.slug}</code></> : null}.
              </div>
            ) : null}
            {linkList.length > 0 ? (
              <div>
                <strong>{linkList.length} shop link{linkList.length === 1 ? "" : "s"}</strong> will be
                shown under the article.
              </div>
            ) : null}
            {actionError ? <div className="alert error">{actionError}</div> : null}
            <div style={{ display: "flex", gap: "var(--space-2)", justifyContent: "flex-end" }}>
              <button
                type="button"
                className="button tinted"
                onClick={() => setPublishModalOpen(false)}
                disabled={busy}
              >
                Cancel
              </button>
              <button
                type="button"
                className="button"
                onClick={handleConfirmPublish}
                disabled={busy}
              >
                {busy ? "Publishing…" : "Confirm publish to Shopify"}
              </button>
            </div>
          </div>
        </Modal>
      ) : null}
    </section>
  );
}

function DraftTab({ post }: { post: BlogPostDetail }) {
  const wordCount = post.draftMarkdown.trim().split(/\s+/).filter(Boolean).length;

  return (
    <div className="card" style={{ padding: "var(--space-4)" }}>
      {post.draftMarkdown ? (
        <>
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "baseline",
              marginBottom: "var(--space-3)",
            }}
          >
            <h3 style={{ margin: 0 }}>Draft</h3>
            <span style={{ color: "var(--color-text-muted)" }}>
              {wordCount} words · {post.editCount} AI edit{post.editCount === 1 ? "" : "s"}
            </span>
          </div>
          <pre
            style={{
              whiteSpace: "pre-wrap",
              fontFamily: "inherit",
              lineHeight: 1.6,
              margin: 0,
            }}
          >
            {post.draftMarkdown}
          </pre>
          <SeoFields post={post} />
        </>
      ) : (
        <p style={{ color: "var(--color-text-muted)", margin: 0 }}>
          The draft will appear here once the writer has researched and written it. That takes a
          few minutes; the &quot;Last event&quot; line above follows its progress and this page refreshes
          on its own.
        </p>
      )}
    </div>
  );
}

/** G-82: the SEO fields the writer submits explicitly; published with the article. */
function SeoFields({ post }: { post: BlogPostDetail }) {
  if (!post.metaTitle && !post.metaDescription && post.tags.length === 0) return null;
  const rows: Array<[string, string | null]> = [
    ["Meta title", post.metaTitle],
    ["Meta description", post.metaDescription],
    ["URL handle", post.slug ? `/blogs/news/${post.slug}` : null],
    ["Excerpt", post.excerpt],
    ["Tags", post.tags.length > 0 ? post.tags.join(", ") : null],
    ["Photo brief", post.heroAlt],
  ];
  return (
    <div
      style={{
        marginTop: "var(--space-4)",
        paddingTop: "var(--space-3)",
        borderTop: "1px solid var(--color-border, #ddd)",
      }}
    >
      <h3 style={{ margin: "0 0 var(--space-2)" }}>SEO fields</h3>
      <dl
        style={{
          display: "grid",
          gridTemplateColumns: "minmax(110px, max-content) 1fr",
          gap: "var(--space-1) var(--space-3)",
          margin: 0,
        }}
      >
        {rows
          .filter(([, value]) => value)
          .map(([label, value]) => (
            <div key={label} style={{ display: "contents" }}>
              <dt style={{ color: "var(--color-text-muted)" }}>{label}</dt>
              <dd style={{ margin: 0 }}>{value}</dd>
            </div>
          ))}
      </dl>
      {post.faq && post.faq.length > 0 ? (
        <div style={{ marginTop: "var(--space-3)" }}>
          <strong>FAQ ({post.faq.length})</strong>
          <ul style={{ margin: "var(--space-1) 0 0", paddingLeft: "var(--space-4)" }}>
            {post.faq.map((item, index) => (
              <li key={`${index}-${item.question}`}>
                <strong>{item.question}</strong>{" "}
                <span style={{ color: "var(--color-text-muted)" }}>{item.answer}</span>
              </li>
            ))}
          </ul>
        </div>
      ) : null}
    </div>
  );
}

function ResearchTab({
  post,
  linkList,
}: {
  post: BlogPostDetail;
  linkList: string[];
}) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "var(--space-3)" }}>
      <div className="card" style={{ padding: "var(--space-4)" }}>
        <h3 style={{ marginTop: 0 }}>🔗 Internal links you provided ({linkList.length})</h3>
        {linkList.length === 0 ? (
          <p style={{ color: "var(--color-text-muted)", margin: 0 }}>None provided.</p>
        ) : (
          <ul style={{ margin: 0, paddingLeft: "var(--space-4)" }}>
            {linkList.map((url) => (
              <li key={url}>
                <a href={url} target="_blank" rel="noopener noreferrer">
                  {url}
                </a>
              </li>
            ))}
          </ul>
        )}
      </div>

      <div className="card" style={{ padding: "var(--space-4)" }}>
        <h3 style={{ marginTop: 0 }}>
          🔍 Search queries generated{" "}
          {post.searchQueries ? `(${post.searchQueries.length})` : ""}
        </h3>
        {post.searchQueries && post.searchQueries.length > 0 ? (
          <ul style={{ margin: 0, paddingLeft: "var(--space-4)" }}>
            {post.searchQueries.map((q) => (
              <li key={q}>{q}</li>
            ))}
          </ul>
        ) : (
          <p style={{ color: "var(--color-text-muted)", margin: 0 }}>
            Pending. The writer searches for what real shoppers type about this idea and lists
            the queries it ran when it submits the draft.
          </p>
        )}
      </div>

      <div className="card" style={{ padding: "var(--space-4)" }}>
        <h3 style={{ marginTop: 0 }}>
          🌐 Sources reviewed{" "}
          {post.researchSources ? `(${post.researchSources.length})` : ""}
        </h3>
        {post.researchSources && post.researchSources.length > 0 ? (
          <table>
            <thead>
              <tr>
                <th>#</th>
                <th>Source</th>
                <th>Title</th>
              </tr>
            </thead>
            <tbody>
              {post.researchSources.map((src, idx) => (
                <tr key={src.url}>
                  <td>{src.rank ?? idx + 1}</td>
                  <td>
                    <a href={src.url} target="_blank" rel="noopener noreferrer">
                      {src.domain ?? new URL(src.url).hostname}
                    </a>
                  </td>
                  <td style={{ color: "var(--color-text-muted)" }}>
                    {src.title ?? src.url}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <p style={{ color: "var(--color-text-muted)", margin: 0 }}>
            Pending. The writer reads the pages that rank for those queries and lists the ones
            it used.
          </p>
        )}
      </div>

      <div className="card" style={{ padding: "var(--space-4)" }}>
        <h3 style={{ marginTop: 0 }}>Brief</h3>
        {post.briefMarkdown || post.outlineMarkdown ? (
          <pre
            style={{
              whiteSpace: "pre-wrap",
              fontFamily: "inherit",
              margin: 0,
              color: "var(--color-text-muted)",
            }}
          >
            {post.briefMarkdown ?? post.outlineMarkdown}
          </pre>
        ) : (
          <p style={{ color: "var(--color-text-muted)", margin: 0 }}>
            Pending. Before drafting, the writer sets down the search intent, the audience, what
            competitors cover, the gap this article owns, the keywords and the FAQ plan.
          </p>
        )}
      </div>

      <div className="card" style={{ padding: "var(--space-4)" }}>
        <h3 style={{ marginTop: 0 }}>
          ✨ Style guard adjustments{" "}
          {post.styleGuardFixes ? `(${post.styleGuardFixes.length})` : ""}
        </h3>
        {post.styleGuardFixes && post.styleGuardFixes.length > 0 ? (
          <ul style={{ margin: 0, paddingLeft: "var(--space-4)" }}>
            {post.styleGuardFixes.map((f) => (
              <li key={f.pattern}>
                <code>{f.pattern}</code>: {f.count} fix{f.count === 1 ? "" : "es"}
              </li>
            ))}
          </ul>
        ) : (
          <p style={{ color: "var(--color-text-muted)", margin: 0 }}>
            None. The CRM checks every submission for em dashes, curly quotes, emojis, banned
            phrases and structure; the writer fixes what it reports before the draft is accepted.
          </p>
        )}
      </div>
    </div>
  );
}

function TimelineTab({ post }: { post: BlogPostDetail }) {
  return (
    <div className="card table-scroll">
      <table>
        <thead>
          <tr>
            <th>When</th>
            <th>From</th>
            <th>To</th>
            <th>Actor</th>
            <th>Details</th>
            <th>Cost</th>
          </tr>
        </thead>
        <tbody>
          {post.runLog.length === 0 ? (
            <tr>
              <td colSpan={6} style={{ textAlign: "center", color: "var(--color-text-muted)" }}>
                No timeline entries yet.
              </td>
            </tr>
          ) : (
            post.runLog.map((entry) => (
              <tr key={entry.id}>
                <td>{formatDateTime(entry.createdAt)}</td>
                <td>{entry.fromState}</td>
                <td>{entry.toState}</td>
                <td>{entry.actor}</td>
                <td style={{ color: "var(--color-text-muted)" }}>{entry.details ?? "—"}</td>
                <td>${entry.costUsd}</td>
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  );
}
